<template>
    <CompassModal
        v-model:visible="showModal"
        :size="CompassEnums.ModalSize.SMALL"
        class="adyen-modal"
        :closeOnClickOverlay="false"
        :primaryAction="primaryAction"
        :cancelButtonProps="cancelButtonProps"
        @closed="emit('close')"
    >
        <template #header>
            <div class="adyen-modal__header">
                <div class="adyen-modal__header-title">
                    <img
                        class="adyen-modal__header-icon"
                        src="/ressources/logged/img/illustrations/payment/providerslogo/adyenlogo.svg"
                        alt="Adyen"
                        width="56"
                        height="56"
                    />
                    <h2 class="typographies-heading-h2 adyen-modal__title">Adyen</h2>
                    <p class="typographies-paragraph-medium-regular adyen-modal__description">
                        {{ translations.connectDescription }}
                    </p>
                </div>
                <div class="adyen-modal__header-close">
                    <CompassIconButton icon="close" @click="emit('close')" />
                </div>
            </div>
        </template>
        <template #default>
            <div class="adyen-modal__body">
                <AdyenModalLoader v-if="loading" />
                <template v-else>
                    <CompassAlert
                        v-if="errorOnAdyen"
                        :type="CompassEnums.AlertType.ERROR"
                        :showClose="false"
                        :description="translations.unavailableError"
                    />
                    <div class="adyen-modal__section">
                        <p class="typographies-paragraph-large-bold adyen-modal__section-title">
                            {{ translations.installationTitle }}
                        </p>
                        <p
                            class="typographies-paragraph-medium-regular adyen-modal__section-description"
                        >
                            {{ translations.installationDescription }}
                        </p>
                        <CompassLink
                            :label="translations.viewDocumentation"
                            link="https://help.sellsy.com/fr/articles/5876306-paiement-en-ligne-des-factures-avec-adyen"
                            target="_blank"
                        >
                            <template #prefix>
                                <span class="typographies-heading-h4 adyen-modal__help-link">
                                    {{ translations.needHelp }}
                                </span>
                            </template>
                        </CompassLink>
                    </div>
                    <CompassFormItem
                        :label="translations.apiKeyLabel"
                        required
                        :error="formErrors.api_key"
                    >
                        <CompassInput
                            v-model="adyenForm.api_key"
                            :error="!!formErrors.api_key"
                            @focus="formErrors.api_key = ''"
                        />
                    </CompassFormItem>
                    <CompassFormItem
                        :label="translations.hmacKeyLabel"
                        required
                        :error="formErrors.notification_hmac_key"
                    >
                        <CompassInput
                            v-model="adyenForm.notification_hmac_key"
                            :type="CompassEnums.InputType.TEXTAREA"
                            :error="!!formErrors.notification_hmac_key"
                            @focus="formErrors.notification_hmac_key = ''"
                        />
                    </CompassFormItem>
                    <CompassFormItem :label="translations.environmentLabel" required>
                        <CompassSelect v-model="adyenForm.test_env" menuClass="select-adyen-modal">
                            <CompassDropdownOption
                                v-for="(option, index) in envOptions"
                                :key="index"
                                :label="option.label"
                                :value="option.value"
                            />
                        </CompassSelect>
                    </CompassFormItem>
                    <CompassFormItem
                        v-if="!adyenForm.test_env"
                        :label="translations.apiUrlPrefixLabel"
                        required
                        :error="formErrors.live_url_prefix"
                    >
                        <CompassInput
                            v-model="adyenForm.live_url_prefix"
                            :error="!!formErrors.live_url_prefix"
                            @focus="formErrors.live_url_prefix = ''"
                        />
                    </CompassFormItem>
                    <CompassFormItem
                        :label="translations.merchantAccountLabel"
                        required
                        :error="formErrors.merchant_account"
                    >
                        <CompassInput
                            v-model="adyenForm.merchant_account"
                            :error="!!formErrors.merchant_account"
                            @focus="formErrors.merchant_account = ''"
                        />
                    </CompassFormItem>
                    <CompassFormItem :label="translations.themeIdLabel">
                        <CompassInput v-model="adyenForm.theme_id" />
                    </CompassFormItem>
                    <CompassFormItem
                        :label="translations.paymentMethodLabel"
                        required
                        :info="translations.paymentMethodInfo"
                        :error="formErrors.payment_method_id"
                    >
                        <CompassSelect
                            v-model="adyenForm.payment_method_id"
                            menuClass="select-adyen-modal"
                        >
                            <CompassDropdownOption
                                v-for="option in meansOfPaymentOptions"
                                :key="option.id"
                                :label="option.label"
                                :value="option.id"
                            />
                        </CompassSelect>
                    </CompassFormItem>
                </template>
            </div>
        </template>
    </CompassModal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject } from 'vue';
import type { Translator } from 'types/injects/translator.type';
import type { PushToast } from 'types/injects/pushToast.type';
import { getSettings, saveSettings } from 'api/plugins/adyenService';
import Payments from 'api/payments.js';
import AdyenModalLoader from 'vcomponents/settings/adyen/AdyenModalLoader.vue';

const emit = defineEmits<{
    close: [];
}>();

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const translations: Record<string, string> = {
    connectDescription: $t.__('settings.adyen.AdyenPopup.connect_description'),
    unavailableError: $t.__('settings.adyen.AdyenPopup.unavailable_error'),
    installationTitle: $t.__('settings.adyen.AdyenPopup.installation_title'),
    installationDescription: $t.__('settings.adyen.AdyenPopup.installation_description'),
    viewDocumentation: $t.__('settings.adyen.AdyenPopup.view_documentation'),
    needHelp: $t.__('common.labels.need_help'),
    apiKeyLabel: $t.__('settings.adyen.AdyenPopup.api_key_label'),
    hmacKeyLabel: $t.__('settings.adyen.AdyenPopup.hmac_key_label'),
    environmentLabel: $t.__('settings.adyen.AdyenPopup.environment_label'),
    apiUrlPrefixLabel: $t.__('settings.adyen.AdyenPopup.api_url_prefix_label'),
    merchantAccountLabel: $t.__('settings.adyen.AdyenPopup.merchant_account_label'),
    themeIdLabel: $t.__('settings.adyen.AdyenPopup.theme_id_label'),
    paymentMethodLabel: $t.__('settings.adyen.AdyenPopup.payment_method_label'),
    paymentMethodInfo: $t.__('settings.adyen.AdyenPopup.payment_method_info'),
    cancel: $t.__('common.actions.cancel'),
    save: $t.__('common.actions.save'),
    fieldRequired: $t.__('common.labels.this_field_is_obligatory'),
    settingsSaved: $t.__('common.labels.settings_saved'),
    toastError: $t.__('common.labels.toast.error'),
};

const showModal = ref(true);
const loading = ref(true);
const isFormSubmitted = ref(false);
const adyenForm = ref<Record<string, unknown>>({});
const errorOnAdyen = ref(false);
const shouldUpdateSettings = ref(true);
const formErrors = ref<Record<string, string>>({});
const meansOfPaymentOptions = ref<{ id: number; label: string }[] | null>(null);

const envOptions: { value: boolean; label: string }[] = [
    { value: false, label: 'Production' },
    { value: true, label: 'Test' },
];

const primaryAction = computed((): { label: string; loading: boolean; action: () => void } => ({
    label: translations.save,
    loading: isFormSubmitted.value,
    action: () => submitForm(),
}));

const cancelButtonProps = {
    label: translations.cancel,
};

const requiredFields = ['api_key', 'notification_hmac_key', 'merchant_account'] as const;

const validate = (): boolean => {
    formErrors.value = {};
    let isValid = true;

    for (const field of requiredFields) {
        const value =
            typeof adyenForm.value[field] === 'string'
                ? (adyenForm.value[field] as string).trim()
                : '';
        if (!value) {
            formErrors.value[field] = translations.fieldRequired;
            isValid = false;
        }
    }

    if (!adyenForm.value.test_env) {
        const liveUrlPrefix =
            typeof adyenForm.value.live_url_prefix === 'string'
                ? (adyenForm.value.live_url_prefix as string).trim()
                : '';
        if (!liveUrlPrefix) {
            formErrors.value.live_url_prefix = translations.fieldRequired;
            isValid = false;
        }
    }

    return isValid;
};

const submitForm = async (): Promise<void> => {
    if (!validate()) {
        return;
    }

    isFormSubmitted.value = true;
    try {
        if (adyenForm.value.test_env) {
            adyenForm.value.live_url_prefix = null;
        }
        await saveSettings(shouldUpdateSettings.value, adyenForm.value);
        $pushToast({
            type: 'success',
            icon: true,
            text: translations.settingsSaved,
        });
        emit('close');
    } catch (error: unknown) {
        const apiError = error as {
            response?: { data?: { error?: { details?: Record<string, string> } } };
        };
        const details = apiError.response?.data?.error?.details;
        if (details) {
            for (const key of Object.keys(details)) {
                if (key === 'more') {
                    errorOnAdyen.value = true;
                } else {
                    formErrors.value = {
                        ...formErrors.value,
                        [key]: details[key],
                    };
                }
            }
        }
        isFormSubmitted.value = false;
    }
};

onMounted(async () => {
    try {
        meansOfPaymentOptions.value = await Payments.getAllPayments();
    } catch {
        $pushToast({
            type: 'error',
            icon: true,
            text: translations.toastError,
        });
        emit('close');
        return;
    }

    try {
        const response = await getSettings();
        adyenForm.value = response.data as Record<string, unknown>;
        if (adyenForm.value.payment_method_id === 0) {
            adyenForm.value.payment_method_id = meansOfPaymentOptions.value?.[0]?.id;
        }
    } catch (error: unknown) {
        const apiError = error as { response?: { data?: { error?: { code?: number } } } };
        if (apiError.response?.data?.error?.code === 404) {
            shouldUpdateSettings.value = false;
            adyenForm.value = {
                test_env: false,
                payment_method_id: meansOfPaymentOptions.value?.[0]?.id,
            };
        } else {
            $pushToast({
                type: 'error',
                icon: true,
                text: translations.toastError,
            });
            emit('close');
        }
    } finally {
        loading.value = false;
    }
});
</script>

<style lang="scss" scoped>
.adyen-modal {
    &__header {
        display: flex;
        justify-content: space-between;
        padding: var(--spacing-4) var(--spacing-5);
    }

    &__header-title {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);
    }

    &__header-icon {
        margin-bottom: var(--spacing-2);
    }

    &__title,
    &__description,
    &__section-title,
    &__section-description {
        margin: 0;
    }

    &__header-close {
        position: absolute;
        top: var(--spacing-3);
        right: var(--spacing-3);
    }

    &__body {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
    }

    &__section {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);
    }

    &__help-link {
        color: var(--colors-common-text-standard-caption);
    }
}
</style>

<style lang="scss">
#compass-dropdown-container {
    .select-adyen-modal {
        z-index: 1001;
    }
}
</style>
